import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen } from '@fortawesome/pro-regular-svg-icons';

import FilterButton from 'components/search_sidebar/filter_button';
import FilterCheckboxGroup from "components/search_sidebar/filter_checkbox_group";
import Tag from 'components/tags/tag';

import AvailabilitiesFilter from './availabilities_filter';
import SaleOrLeaseFilterGroup from './sale_or_lease_filter_group';
import ResearchPropertyFilterGroup from './research_property_filter_group';
import PropertyGroupFilter from './property_group_filter';

import {
  clearFiltersIfEmpty,
  clearFilters,
  selectAssetClass,
  changeSearchText,
  setTags
} from '../store/actions/query';
import { delayRetrieveRecords, retrieveRecords } from '../store/actions/search';
import { retrieveGroups } from "../store/actions/properties";
import { Button } from 'react-bootstrap-5';

import { initialState } from '../research_constants';

export class MobileResearchFilters extends React.Component {
  static propTypes = {
    assetClasses: PropTypes.array.isRequired,
    availabilities: PropTypes.array.isRequired,
    availableTags: PropTypes.array.isRequired,
    canAccessCrm: PropTypes.bool.isRequired,
    clearFiltersIfEmpty: PropTypes.func.isRequired,
    currentTags: PropTypes.array.isRequired,
    delayRetrieveRecords: PropTypes.func,
    groups: PropTypes.array,
    propertyCount: PropTypes.number.isRequired,
    retrieveGroups: PropTypes.func.isRequired,
    retrieveRecords: PropTypes.func.isRequired,
    searchText: PropTypes.string.isRequired,
    selectAssetClass: PropTypes.func.isRequired,
    selectedAssetClasses: PropTypes.array.isRequired,
    selectedGroupIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    setTags: PropTypes.func.isRequired,
    totalUngroupedCount: PropTypes.number.isRequired,

    onChangePropertyFilterGroup: PropTypes.func.isRequired,
    onChangeSaleFilterGroup: PropTypes.func.isRequired,
    onChangeSearchText: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    onCloseFilters: PropTypes.func.isRequired
  };

  componentDidMount() {
    const {
      clearFiltersIfEmpty,
      retrieveGroups,
      retrieveRecords
    } = this.props;

    clearFiltersIfEmpty();
    retrieveRecords();
    retrieveGroups();
  }

  get selectedGroups() {
    const { groups, selectedGroupIds, totalUngroupedCount } = this.props;

    if (selectedGroupIds[0] === 'ungrouped') {
      return [{ id: 'ungrouped', name: 'Ungrouped', count: totalUngroupedCount }];
    } else {
      return groups.filter(group => selectedGroupIds.includes(group.id));
    }
  }

  handleAddTag = (tag) => {
    const { currentTags } = this.props;
    currentTags.push(tag);
    this.handleTagChange(currentTags);
  }

  handleRemoveTag = (tag) => {
    const { currentTags } = this.props;
    const newTags = currentTags.filter(t => t !== tag);
    this.handleTagChange(newTags);
  }

  handleRetrieveRecords = () => {
    const { clearFiltersIfEmpty, delayRetrieveRecords } = this.props;
    clearFiltersIfEmpty();
    delayRetrieveRecords();
  }

  handleTagChange = (tags) => {
    const { setTags } = this.props;
    setTags(tags);
    this.handleRetrieveRecords();
  }

  handleAssetClassChange = (e) => {
    const { selectAssetClass } = this.props;
    selectAssetClass(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  handleSearchChange = (e) => {
    const { onChangeSearchText } = this.props;
    onChangeSearchText(e.target.value);
    this.handleRetrieveRecords();
  };

  handleClearFilters = () => {
    const {
      delayRetrieveRecords,
      onClearFilters
    } = this.props;

    onClearFilters(initialState);
    delayRetrieveRecords();
  };

  get groupFilterLabel() {
    const selectedGroups = this.selectedGroups;

    if (selectedGroups.length === 0) {
      return 'All Properties';
    } else if (selectedGroups.length === 1) {
      return selectedGroups[0].name;
    } else {
      return `${selectedGroups.length} Groups`;
    }
  }

  get groupFilterCount() {
    const selectedGroups = this.selectedGroups;

    if (selectedGroups.length === 0) {
      return this.props.propertyCount;
    } else if (selectedGroups[0].id === 'ungrouped') {
      return selectedGroups[0].count;
    } else {
      return selectedGroups.reduce((count, group) => count + group.count, 0);
    }
  }

  render() {
    const { canAccessCrm, onCloseFilters, groups } = this.props;
    return (
      <div className="d-flex flex-column gap-4 p-3 position-fixed start-0 end-0 top-0 bottom-0 bg-white overflow-auto z-3">
        <Button variant="outline" onClick={this.handleClearFilters}>Clear Filters</Button>
        {canAccessCrm && groups.length > 0 && this.renderGroupButton()}
        {this.renderPropertyTypeFilter()}
        {this.renderSaleFilterGroup()}
        {this.renderAvailabilitiesFilter()}
        {this.renderPropertyFilterGroup()}
        {this.renderTagsFilter()}
        <Button name="view-properties" onClick={onCloseFilters}>View Properties</Button>
      </div>
    );
  }

  // Group logic (not just here) is copied 4 to 6 times - properties, contacts (people and companies), mobile and non
  // TODO: Extract reusable components
  renderGroupFilterLabel() {
    let count;
    if (this.selectedGroups.length <= 1) {
      count = (
        <span>
          ({this.groupFilterCount})
        </span>
      );
    }

    return (
      <React.Fragment>
        <FontAwesomeIcon
          icon={faFolderOpen}
        />
        <span className="group-text">
          {this.groupFilterLabel}
          {count}
        </span>
      </React.Fragment>
    );
  }

  renderGroupButton() {
    return (
      <FilterButton
        filtersApplied={false}
        label={this.renderGroupFilterLabel()}
        name="groups"
      >
        <PropertyGroupFilter />
      </FilterButton>
    );
  }

  renderAvailabilitiesFilter() {
    const { availabilities } = this.props;
    return (
      <React.Fragment>
        <h6 className="fs-large fw-semibold pb-1 border-bottom">Availability</h6>
        <AvailabilitiesFilter availabilities={availabilities} />
      </React.Fragment>
    );
  }

  renderPropertyTypeFilter() {
    const { assetClasses, selectedAssetClasses } = this.props;
    return (
      <div className="d-flex flex-column gap-2">
        <h6 className="fs-large fw-semibold pb-1 border-bottom">Property Type</h6>
        <FilterCheckboxGroup
          defaultKey="mobile"
          options={assetClasses}
          selected={selectedAssetClasses}
          onChange={this.handleAssetClassChange}
        />
      </div>
    );
  }

  renderPropertyFilterGroup() {
    return (
      <div className="d-flex flex-column gap-2">
        <h6 className="fs-large fw-semibold pb-1 border-bottom">Property & Building</h6>
        <ResearchPropertyFilterGroup />
      </div>
    );
  }

  renderTagsFilter() {
    const { availableTags, currentTags } = this.props;

    const tagList = [];
    availableTags.forEach((availableTag) => {
      if (currentTags.findIndex(tag => tag === availableTag) >= 0) {
        tagList.push(
          <div
            className="d-inline-block active-tag clickable"
            key={availableTag}
            onClick={() => this.handleRemoveTag(availableTag)}
          >
            <Tag>{availableTag}</Tag>
          </div>
        );
      } else {
        tagList.push(
          <div
            className="d-inline-block inactive-tag clickable"
            key={availableTag}
            onClick={() => this.handleAddTag(availableTag)}
          >
            <Tag>{availableTag}</Tag>
          </div>
        );
      }
    });

    if (tagList.length > 0) {
      return (
        <React.Fragment>
          <h6 className="fs-large fw-semibold pb-1 border-bottom">Tags</h6>
          {tagList}
        </React.Fragment>
      );
    }
  }

  renderSaleFilterGroup() {
    return (
      <React.Fragment>
        <h6 className="fs-large fw-semibold pb-1 border-bottom">Sale/Lease</h6>
        <SaleOrLeaseFilterGroup />
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  canAccessCrm: state.ui.canAccessCrm,
  propertyCount: state.search.meta.totalCount,
  assetClasses: state.static.assetClasses,
  availabilities: state.static.availabilities,
  availableTags: state.static.tags,
  currentTags: state.query.tags,
  groups: state.properties.groups,
  searchText: state.query.searchText,
  selectedAssetClasses: state.query.assetClasses,
  selectedGroupIds: state.query.researchGroupIds,
  totalUngroupedCount: state.search.meta.totalUngroupedCount
});

const mapDispatchToProps = {
  clearFiltersIfEmpty,
  delayRetrieveRecords,
  retrieveGroups,
  retrieveRecords,
  selectAssetClass,
  setTags,

  onClearFilters: clearFilters,
  onChangeSearchText: changeSearchText,
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileResearchFilters);
